/**
 * Shared PvP Roulette wheel: canvas + overlay (countdown, phase text).
 * Used by /game and /dev/room-emulator.
 */

import { useEffect, useMemo, useRef } from "react"
import { useWheelEngine } from "@/features/pvp-roulette-wheel/engine/WheelEngineContext"
import { usePvpRoomCommands, usePvpRoomsStore } from "@/entities/pvp-roulette"
import { WheelCenterOverlay } from "./WheelCenterOverlay"
import type { WheelEngineOptions } from "@/features/pvp-roulette-wheel/engine"
import { getUserIdFromToken } from "@/shared/api/auth"
import { resolveWheelUserId } from "@/shared/lib/pvpWheelUserIdentity"
import { TEAM_PATTERN_SRC } from "@/features/pvp-roulette-wheel/assets/teamPattern"
import { usePvpRouletteRuntimeRevision } from "@/shared/lib/pvp-roulette-runtime/useRuntimeRevision"
import { usePvpWheelBootstrapStore } from "@/shared/store"

interface PvPRouletteWheelProps {
  roomId: string | null
  engineOptions?: Partial<WheelEngineOptions>
  className?: string
  /** Override the "current user" for glow rendering. Falls back to getUserIdFromToken(). */
  currentUserId?: string | null
  /** Optional external clock for center overlay countdown/progress (dev emulator). */
  overlayNowMs?: number | null
}

export function PvPRouletteWheel({
  roomId,
  engineOptions,
  className,
  currentUserId: currentUserIdProp,
  overlayNowMs = null,
}: PvPRouletteWheelProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const backMountRef = useRef<HTMLDivElement>(null)
  const frontMountRef = useRef<HTMLDivElement>(null)
  const engine = useWheelEngine()
  /** Avoid releasing `pixiInitDone` on React Strict Mode's fake unmount when the wheel remounts immediately. */
  const wheelMountAliveRef = useRef(false)
  const commands = usePvpRoomCommands(roomId)
  const ackCommands = usePvpRoomsStore((s) => s.ackCommands)
  const rawRound = usePvpRoomsStore((s) =>
    roomId ? s.rooms[roomId]?.rawRound : null
  )
  const optimisticTrickPending = usePvpRoomsStore((s) =>
    roomId ? s.rooms[roomId]?.optimisticTrickPending : null
  )
  const resolvedUserId =
    currentUserIdProp !== undefined ? currentUserIdProp : getUserIdFromToken()
  const myTrickCount = useMemo(() => {
    if (!rawRound || resolvedUserId == null) return 0
    const fromServer = rawRound.tricks.filter(
      (t) => t.userId === resolvedUserId
    ).length
    if (
      optimisticTrickPending &&
      optimisticTrickPending.userId === resolvedUserId &&
      optimisticTrickPending.roundId === rawRound.roundId &&
      fromServer === 0
    ) {
      return 1
    }
    return fromServer
  }, [rawRound, resolvedUserId, optimisticTrickPending])

  const optionsKey = engineOptions
    ? `${engineOptions.minSpinDurationMs ?? ""}-${engineOptions.minSpinRotations ?? ""}`
    : ""
  const pvpRtRev = usePvpRouletteRuntimeRevision()

  useEffect(() => {
    if (pvpRtRev <= 0) return
    engine?.reapplyRuntimeLayout()
  }, [pvpRtRev, engine])

  useEffect(() => {
    const el = containerRef.current
    const backMount = backMountRef.current
    const frontMount = frontMountRef.current
    if (!el || !backMount || !frontMount || !roomId || !engine) return

    wheelMountAliveRef.current = true
    let cancelled = false
    usePvpWheelBootstrapStore.getState().setPixiInitDone(false)

    void (async () => {
      try {
        await engine.init(el, {
          ...engineOptions,
          layers: { back: backMount, front: frontMount },
        })
        if (cancelled) return
        usePvpWheelBootstrapStore.getState().setPixiInitDone(true)
        await engine.setTeamPatternSrc(TEAM_PATTERN_SRC)
        if (cancelled) return
        const slice = usePvpRoomsStore.getState().rooms[roomId]
        const hydrateUserId =
          resolvedUserId ??
          (slice?.rawRound ? resolveWheelUserId(slice.rawRound) : null) ??
          getUserIdFromToken() ??
          undefined
        engine.setCurrentUserId(hydrateUserId)
        if (slice?.rawRound) {
          engine.reconcileWithRoomSlice({
            snapshot: slice.rawRound,
            pendingCommandsCount: slice.commandsQueue.length,
            userId: hydrateUserId,
          })
        }
      } catch (err) {
        console.error("[PvPRouletteWheel] engine.init failed:", err)
        if (!cancelled) {
          engine.setCurrentUserId(resolvedUserId)
          usePvpWheelBootstrapStore.getState().setPixiInitDone(true)
        }
      }
    })()

    return () => {
      cancelled = true
      wheelMountAliveRef.current = false
      engine.detach()
      queueMicrotask(() => {
        if (!wheelMountAliveRef.current) {
          usePvpWheelBootstrapStore.getState().setPixiInitDone(true)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- optionsKey tracks engineOptions; resolvedUserId via sibling effect
  }, [optionsKey, roomId, resolvedUserId, engine])

  useEffect(() => {
    const el = containerRef.current
    if (!engine || !roomId || !el) return

    let rafId = 0
    const apply = () => {
      rafId = 0
      engine.syncLayoutToContainer()
    }
    const schedule = () => {
      if (rafId !== 0) return
      rafId = requestAnimationFrame(apply)
    }

    const ro = new ResizeObserver(schedule)
    ro.observe(el)
    const vv = window.visualViewport
    vv?.addEventListener("resize", schedule)
    window.addEventListener("orientationchange", schedule)
    schedule()

    return () => {
      ro.disconnect()
      vv?.removeEventListener("resize", schedule)
      window.removeEventListener("orientationchange", schedule)
      cancelAnimationFrame(rafId)
    }
  }, [engine, roomId])

  useEffect(() => {
    if (!engine) return
    engine.setCurrentUserId(resolvedUserId)
  }, [resolvedUserId, engine])

  const prevMyTrickCountRef = useRef(-1)
  const prevRoomIdForTrickRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevRoomIdForTrickRef.current !== roomId) {
      prevRoomIdForTrickRef.current = roomId
      prevMyTrickCountRef.current = -1
    }
    if (!roomId || !rawRound || !engine) return
    if (prevMyTrickCountRef.current < 0) {
      prevMyTrickCountRef.current = myTrickCount
      return
    }
    if (
      myTrickCount < prevMyTrickCountRef.current &&
      rawRound.status === "TRICKS" &&
      resolvedUserId != null
    ) {
      engine.resyncAfterOwnTrickRemoved(rawRound, resolvedUserId)
    }
    prevMyTrickCountRef.current = myTrickCount
  }, [roomId, rawRound, myTrickCount, resolvedUserId, engine])

  useEffect(() => {
    if (!roomId || commands.length === 0 || !engine) return

    engine.apply(commands)
    const lastId = commands[commands.length - 1]?.id
    if (lastId) ackCommands(roomId, lastId)
  }, [commands, roomId, ackCommands, engine])

  useEffect(() => {
    if (!roomId || !rawRound || !engine) return
    const userId =
      resolvedUserId ??
      resolveWheelUserId(rawRound) ??
      getUserIdFromToken() ??
      undefined
    engine.reconcileWithRoomSlice({
      snapshot: rawRound,
      pendingCommandsCount: commands.length,
      userId,
    })
  }, [roomId, rawRound, commands.length, resolvedUserId, engine])

  if (!roomId) return null

  return (
    <div
      ref={containerRef}
      className={`relative mx-auto -mt-6 aspect-square w-full max-w-[400px] overflow-visible ${className ?? ""}`}
    >
      <div
        ref={backMountRef}
        className="pointer-events-none absolute inset-0 z-0"
        aria-hidden
      />
      <WheelCenterOverlay roomId={roomId} nowMs={overlayNowMs} />
      <div
        ref={frontMountRef}
        className="pointer-events-none absolute inset-0 z-20"
        aria-hidden
      />
    </div>
  )
}
